// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/* eslint-disable spire/fenced-imports */
import TippyReact, { TippyProps as TippyReactProps } from "@tippyjs/react";
import { Instance as TippyInstance } from "tippy.js";
/* eslint-enable spire/fenced-imports */

import { injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { importTippyCssFiles } from "@west/App/Components/Tippy/tippy.helper";
import { getSystemZIndex } from "@west/XDL/Utilities";
import { mergeWithCss } from "@west/XDL/Utilities/mergeWithCss";
import React, { FC, useEffect, useState } from "react";
import styled from "styled-components";

export const TippyWrapper = styled(TippyReact)<TippyReactProps>`
    ${injectCss}
`;

export interface TippyProps extends TippyReactProps {
    onShowTrigger?: (isOpen: boolean) => void;
    excludeTriggerElement?: boolean;
    extendedStyles?: TippyStyles;
}

type Props = TippyProps;

const TippyTrigger = styled.span.attrs({ tabIndex: 0, role: "tooltip" })<InjectableCss<any>>`
    outline: none;
    ${injectCss}
`;

export interface TippyStyles {
    container?: InjectableCss;
    trigger?: InjectableCss;
}

export const tippyStyles: TippyStyles = {};

const Tippy: FC<Props> = ({
    onShowTrigger,
    extendedStyles,
    children,
    content: tippyContent,
    excludeTriggerElement,
    ...tippyProps
}) => {
    const [styles] = useState(() => mergeWithCss(tippyStyles, extendedStyles));
    const [instance, setInstance] = useState<TippyInstance>();

    useEffect(() => {
        importTippyCssFiles();
    }, []);

    const closeButtonHandler = () => {
        instance?.hide?.();
    };

    const onCreateHandler = (instance: TippyInstance) => {
        setInstance(instance);
        tippyProps.onCreate?.(instance);
    };

    const onShowHandler = (instance: TippyInstance) => {
        tippyProps.onShow?.(instance);
        onShowTrigger?.(true);
    };

    const onHideHandler = (instance: TippyInstance) => {
        tippyProps.onHide?.(instance);
        onShowTrigger?.(false);
    };

    return (
        <TippyWrapper
            {...tippyProps}
            onCreate={onCreateHandler}
            zIndex={getSystemZIndex("tippy")}
            onShow={onShowHandler}
            onHide={onHideHandler}
            content={tippyContent}
            {...styles.container}
        >
            {excludeTriggerElement ? children : <TippyTrigger {...styles.trigger}>{children}</TippyTrigger>}
        </TippyWrapper>
    );
};

Tippy.defaultProps = {
    interactive: true,
    offset: [0, 20],
};

export default Tippy;
